import {makeCresAxiosFoundationAPI} from "./AxiosFoundationAPI";
import {LIFECYCLE_STATUSES, statusVocabularyFor} from "./objectStatuses";

// Counts for an object list's left pane, for any classification.
//
// There is no precomputed statistics table for objects the way the community pages have
// CommunityAnswerStatistic, so these are read live: one count query per status. Each request asks for
// Limit 1 and reads only the IDOC envelope's Count, so the rows themselves are never transferred.
//
// That is one small request per status on page load (six today). It is worth stating plainly rather
// than hiding: if the record volume or the status list grows enough for that to hurt, the answer is a
// precomputed statistics row per realm, the same shape the community pages already use -- not a
// bigger query here.

export type ObjectStatRow = {label: string; status: string; count: number};

/** One OBJECT_TYPE of this classification. The ACRONYM is the identity here: it is what the sidebar
 *  offers, what the query filters on, and what the object number is built from ("SOP-00001"), so a
 *  type without one cannot be filtered to and is left out rather than shown as a blank row. */
export type ObjectTypeRow = {
    usid: string; acronym: string; name: string; count: number;
    /** The type this one specialises, or null for a root. Lets the pane draw the taxonomy as the
     *  tree it is, and lets ticking a parent mean its descendants. */
    parentUSID: string | null;
};

/** Every type at or below these, by acronym.
 *
 *  TICKING A PARENT MEANS ITS DESCENDANTS, which is the whole point of a taxonomy: somebody
 *  filtering a parts list to "Screw" wants screws, not the handful of records whose type is
 *  literally the abstract parent. Expanded in ONE place and the expansion is what is sent -- to the
 *  grid and to the facet counts alike, so the numbers in the pane and the rows in the list can
 *  never describe different sets.
 *
 *  Cycle-safe by the visited set, not by trusting the data: the server refuses to write a cycle,
 *  but a pane that hangs because one exists would be a bad way to find that out. */
export function withDescendants(types: ObjectTypeRow[], acronyms: Iterable<string>): string[] {
    const byParent = new Map<string, ObjectTypeRow[]>();
    for (const type of types) {
        const key = type.parentUSID ?? "";
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key)!.push(type);
    }

    const wanted = new Set<string>();
    const seen = new Set<string>();
    const queue = types.filter(one => [...acronyms].includes(one.acronym));

    while (queue.length > 0) {
        const type = queue.shift()!;
        if (seen.has(type.usid)) continue;
        seen.add(type.usid);
        wanted.add(type.acronym);
        for (const child of byParent.get(type.usid) ?? []) queue.push(child);
    }

    // An acronym with no row -- a type filtered out as inactive, say -- is passed through rather
    // than dropped. Dropping it would silently widen the filter to everything.
    for (const acronym of acronyms) wanted.add(acronym);
    return Array.from(wanted);
}

export type ObjectStatistics = {
    statuses: ObjectStatRow[];
    /** Empty for a classification that defines no types -- the sidebar then shows no Type section
     *  rather than an empty heading. */
    types: ObjectTypeRow[];
};

// The revision lifecycle, kept under its old name for the sidebar's default. The list itself lives
// beside DEFAULT_STATUSES in objectStatuses.ts, because the vocabulary and the default ticks must be
// drawn from the same place or a classification whose words differ (a Project is OPEN or CLOSED)
// gets one and not the other.
export const OBJECT_STATUSES = LIFECYCLE_STATUSES;

// The grouping tag is absent entirely when nothing matches, so a missing Count means zero rather
// than an error.
function countOf(response: any): number {
    const group = response?.data?.PlatformMessage?.ListOfObjectRevision;
    const count = group?.Count;
    return typeof count === "number" ? count : Number(count ?? 0) || 0;
}

/**
 * Load the sidebar counts. Never rejects: a statistics panel that breaks the page it decorates is
 * worse than one showing dashes, so a failed request resolves to null and the caller falls back.
 */
export async function loadObjectStatistics(classification: string, sessionContext: any): Promise<ObjectStatistics | null> {
    try {
        const api = makeCresAxiosFoundationAPI(sessionContext);
        const active = sessionContext?.getActiveVisibility();
        const headers = {
            "visibility-mode": active?.Mode ?? "",
            "visibility-value": active?.Value ?? "",
        };

        const base = {ObjectClassification: classification, Limit: 1};

        // Per status, deliberately WITHOUT IsCurrent: a superseded, cancelled or ended revision is
        // never the current one, so filtering on both would report zero for half the list.
        const statusRequests = statusVocabularyFor(classification).map(s =>
            api.get("t1/v1/object_revisions", {params: {...base, Status: s.status}, headers})
                .then(r => ({label: s.label, status: s.status, count: countOf(r)}))
                // One failed status must not lose the other five.
                .catch(() => ({label: s.label, status: s.status, count: 0})),
        );

        // THE TYPES ARE FETCHED BEFORE THEY CAN BE COUNTED, because unlike the statuses -- a fixed
        // list this file owns -- the types are the customer's own and only the server knows them.
        // A failure here costs the Type section, not the Status counts beside it.
        const types = await api.get("t1/v1/object_types",
                                    {params: {ObjectClassification: classification, Active: "true", Limit: 200}, headers})
            .then(response => {
                const list = response?.data?.PlatformMessage?.ListOfObjectType?.ObjectType ?? [];
                return (Array.isArray(list) ? list : [list]) as any[];
            })
            .catch(() => [] as any[]);

        // ONE COUNT REQUEST PER TYPE, the same shape as the statuses above and with the same caveat
        // written there: this is a live count, not a precomputed one. It is a handful of requests
        // for a handful of types, and if a customer defines enough types for that to hurt, the
        // answer is the per-realm statistics row -- not a cheaper query here.
        const typeRequests = types
            .filter(t => (t?.Acronym ?? "").trim() !== "")
            .map(t => api.get("t1/v1/object_revisions",
                              {params: {...base, ObjectTypeAcronym: t.Acronym}, headers})
                .then(r => ({usid: t.USID ?? "", acronym: t.Acronym, name: t.Name ?? "",
                             parentUSID: t.ParentObjectTypeUSID ?? null, count: countOf(r)}))
                .catch(() => ({usid: t.USID ?? "", acronym: t.Acronym, name: t.Name ?? "",
                               parentUSID: t.ParentObjectTypeUSID ?? null, count: 0})));

        return {
            statuses: await Promise.all(statusRequests),
            // Alphabetical by acronym: the server returns them in creation order, which is an order
            // nobody scanning the pane can predict.
            types: (await Promise.all(typeRequests)).sort((a, b) => a.acronym.localeCompare(b.acronym)),
        };
    } catch (e) {
        console.error("failed to load object statistics", e);
        return null;
    }
}
